import { PageCanvas } from "@/components/page-canvas";
import { useState } from "react";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  locationRequestPath,
  locationRequestCompletionPath,
  locationRequestCustomerPath,
  editLocationRequestPath,
  locationRequestsPath,
} from "@/routes";
import { Choice, Errors } from "./controls";
import type { Enquiry, Pagination } from "./types";

type Candidate = Pick<
  NonNullable<Enquiry["vehicle"]>,
  "id" | "revision" | "make" | "model" | "registration_number"
>;

type Match = {
  id: string;
  revision: string;
  name: string;
  matched_contacts: string[];
};
export default function Completion({
  enquiry,
  matches,
  vehicle_candidates,
  vehicle_pagination,
  vehicle_query,
}: {
  enquiry: Enquiry;
  matches: Match[];
  vehicle_candidates: Candidate[];
  vehicle_pagination: Pagination;
  vehicle_query: string;
}) {
  const { auth } = usePage().props;
  const { t } = useTranslation();
  const [selected, setSelected] = useState(enquiry.customer?.id || "new");
  const [vehicleId, setVehicleId] = useState(enquiry.vehicle?.id || "new");
  const needsAssociation =
    selected !== "new" && selected !== enquiry.customer?.id;
  const vehicles =
    enquiry.vehicle &&
    !vehicle_candidates.some((vehicle) => vehicle.id === enquiry.vehicle?.id)
      ? [enquiry.vehicle, ...vehicle_candidates]
      : vehicle_candidates;
  const selectedVehicle = vehicles.find((vehicle) => vehicle.id === vehicleId);
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const chosenRevision =
    selected === enquiry.customer?.id
      ? enquiry.customer.revision
      : matches.find((match) => match.id === selected)?.revision;
  return (
    <AppLayout
      breadcrumbs={[
        { title: t("requests.title"), href: locationRequestsPath(locationId) },
      ]}
    >
      <Head title={t("requests.complete_details")} />
      <PageCanvas>
        <h1 className="text-page font-display">
          {t("requests.complete_details")}
        </h1>
        <p className="text-muted-foreground">{t("requests.completion_help")}</p>
        {enquiry.customer && (
          <Form
            action={editLocationRequestPath(locationId, enquiry.id)}
            method="get"
            className="space-y-3"
          >
            <Label htmlFor="request-vehicle-search">
              {t("requests.search_vehicles")}
            </Label>
            <div className="flex gap-3">
              <Input
                id="request-vehicle-search"
                name="vehicle_query"
                defaultValue={vehicle_query}
              />
              <Button type="submit" variant="outline">
                {t("requests.filter")}
              </Button>
            </div>
            <nav
              aria-label={t("requests.pages")}
              className="flex flex-wrap gap-3"
            >
              {vehicle_pagination.page > 1 && (
                <Link
                  className="underline"
                  href={editLocationRequestPath(locationId, enquiry.id, {
                    page: vehicle_pagination.page - 1,
                    vehicle_query,
                  })}
                >
                  {t("requests.previous")}
                </Link>
              )}
              <span>{t("requests.page", vehicle_pagination)}</span>
              {vehicle_pagination.page < vehicle_pagination.pages && (
                <Link
                  className="underline"
                  href={editLocationRequestPath(locationId, enquiry.id, {
                    page: vehicle_pagination.page + 1,
                    vehicle_query,
                  })}
                >
                  {t("requests.next")}
                </Link>
              )}
            </nav>
          </Form>
        )}
        <Card>
          <CardContent className="pt-6">
            <Form
              action={
                needsAssociation
                  ? locationRequestCustomerPath(locationId, enquiry.id)
                  : locationRequestCompletionPath(locationId, enquiry.id)
              }
              method="post"
            >
              {({ processing }) => (
                <div className="space-y-6">
                  <Errors
                    targets={{
                      name: "request-name",
                      email: "request-email",
                      phone: "request-phone",
                      vehicle: "request-make",
                      service_details: "request-service-details",
                    }}
                  />
                  <input
                    type="hidden"
                    name="revision"
                    value={enquiry.revision}
                  />
                  <input
                    type="hidden"
                    name="request[customer_id]"
                    value={selected === "new" ? "" : selected}
                  />
                  <input
                    type="hidden"
                    name="request[customer_revision]"
                    value={chosenRevision || ""}
                  />
                  {matches.length > 0 && (
                    <div className="space-y-3">
                      <p>{t("customers.reuse_help")}</p>
                      <Choice
                        id="request-customer-choice"
                        name="customer_choice"
                        label={t("customers.choose")}
                        defaultValue={selected}
                        onValueChange={(value) => setSelected(value || "new")}
                        options={[
                          { id: "new", name: t("requests.correct_contacts") },
                          ...matches.map((match) => ({
                            id: match.id,
                            name: `${match.name} (${match.matched_contacts.map((contact) => t(`customers.${contact}`)).join(", ")})`,
                          })),
                        ]}
                      />
                    </div>
                  )}
                  <fieldset
                    key={enquiry.customer?.revision || "new"}
                    className="space-y-4"
                  >
                    <legend className="mb-3 font-medium">
                      {t("customers.details")}
                    </legend>
                    {(["name", "email", "phone"] as const).map((field) => (
                      <div key={field} className="space-y-2">
                        <Label htmlFor={`request-${field}`}>
                          {t(`customers.${field}`)}
                        </Label>
                        <Input
                          id={`request-${field}`}
                          name={`request[customer][${field}]`}
                          type={
                            field === "email"
                              ? "email"
                              : field === "phone"
                                ? "tel"
                                : "text"
                          }
                          defaultValue={
                            enquiry.customer?.[field] ||
                            String(enquiry.intake_details?.[field] || "")
                          }
                          required={field === "name" && selected === "new"}
                          autoComplete={field === "phone" ? "tel" : field}
                        />
                        {field === "phone" && (
                          <p className="text-muted-foreground text-sm">
                            {t("customers.phone_help")}
                          </p>
                        )}
                      </div>
                    ))}
                    {selected !== "new" && (
                      <p className="text-muted-foreground text-sm">
                        {t("requests.saved_customer_help")}
                      </p>
                    )}
                  </fieldset>
                  <fieldset className="space-y-4" disabled={needsAssociation}>
                    <legend className="mb-3 font-medium">
                      {t("vehicles.details")}
                    </legend>
                    {enquiry.customer && (
                      <>
                        <p className="text-muted-foreground text-sm">
                          {t("requests.vehicle_help")}
                        </p>
                        <Choice
                          id="request-vehicle-choice"
                          name="vehicle_choice"
                          label={t("requests.choose_vehicle")}
                          defaultValue={vehicleId}
                          onValueChange={(value) =>
                            setVehicleId(value || "new")
                          }
                          options={[
                            { id: "new", name: t("requests.new_vehicle") },
                            ...vehicles.map((vehicle) => ({
                              id: vehicle.id,
                              name: `${vehicle.make} ${vehicle.model}${vehicle.registration_number ? ` · ${vehicle.registration_number}` : ""}`,
                            })),
                          ]}
                        />
                      </>
                    )}
                    <input
                      type="hidden"
                      name="request[vehicle_id]"
                      value={selectedVehicle?.id || ""}
                    />
                    <input
                      type="hidden"
                      name="request[vehicle_revision]"
                      value={selectedVehicle?.revision || ""}
                    />
                    {!selectedVehicle &&
                      (
                        [
                          "make",
                          "model",
                          "fabrication_year",
                          "colour",
                          "registration_number",
                          "vin",
                        ] as const
                      ).map((field) => (
                        <div key={field} className="space-y-2">
                          <Label htmlFor={`request-${field}`}>
                            {t(`vehicles.${field}`)}
                          </Label>
                          <Input
                            id={`request-${field}`}
                            name={`request[vehicle][${field}]`}
                            type={
                              field === "fabrication_year" ? "number" : "text"
                            }
                            required={field === "make" || field === "model"}
                            defaultValue={String(
                              enquiry.intake_details?.[field] || "",
                            )}
                            min={field === "fabrication_year" ? 1 : undefined}
                          />
                        </div>
                      ))}
                  </fieldset>
                  <div className="space-y-2">
                    <Label htmlFor="request-service-details">
                      {t("requests.service_details")}
                    </Label>
                    <Textarea
                      id="request-service-details"
                      name="request[service_details]"
                      defaultValue={enquiry.service_details}
                      required={!needsAssociation}
                      maxLength={10000}
                    />
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t(
                        needsAssociation
                          ? "requests.use_customer"
                          : "requests.save_details",
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      render={
                        <Link
                          href={locationRequestPath(locationId, enquiry.id)}
                        />
                      }
                    >
                      {t("customers.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
